import { JitsiMeeting } from "@jitsi/react-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/primitives/button";
import { Input } from "@/components/primitives/input";
import LoadingScreen from "@/components/shared/LoadingScreen";
import { redeemGuestInvite } from "@/server/services/inviteService";
import { readGuestInviteCodeFromHash } from "@/lib/guestInviteLink";
import { buildMeetToolbarButtons } from "@/components/chat/middle-column/call/jitsiToolbar";
import { featureFlags } from "@/config/featureFlags";

type RedeemState =
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "prejoin";
      code: string;
      token: string;
      room: string;
      inviteeName: string | null;
    }
  | { status: "joined"; token: string; room: string };

/**
 * Public, unauthenticated guest-join page (route `/meet/guest`).
 *
 * The invite code is read from the URL FRAGMENT (`#<code>`), never a path/query
 * segment — fragments never reach the server, access logs, the Referer header,
 * or the navigation breadcrumb, so the secret code can't leak that way. On mount
 * it POSTs the code (via a NO-AUTH client) to `/umbra/meet/invites/redeem`; on
 * success it renders the Jitsi embed directly with the returned guest token +
 * room so the guest lands in the meeting with no account/session.
 *
 * Failure handling is GENERIC by design: invalid / expired / revoked / exhausted
 * all collapse to one friendly "invite is no longer valid" screen — we never
 * reveal which reason applied (matches the backend's single generic error).
 * The raw code is a secret and is never logged.
 */
export default function GuestMeetPage() {
  const { t } = useTranslation();
  const [code, setCode] = useState<string | undefined>(
    readGuestInviteCodeFromHash,
  );
  const [state, setState] = useState<RedeemState>({ status: "loading" });

  // A one-time-use invite must not be redeemed twice. Track WHICH code was
  // already processed (not just a boolean) so:
  //  - StrictMode's dev double-mount runs exactly once (same code → skip), and
  //  - if the fragment changes to a NEW code, that new code IS redeemed (F4).
  // Initial value is `undefined` (a distinct "nothing processed yet" sentinel)
  // so it never collides with a real code (string) OR the absent-code case
  // (normalized to `null`) — otherwise a missing code would be silently skipped.
  const redeemedCodeRef = useRef<string | null | undefined>(undefined);

  // fe#1084 — guest-join pre-join name input. Only read/written when
  // `featureFlags.guestNameInput` is on (state stays "prejoin" otherwise
  // never happens). `isJoining` disables the Join button while the optional
  // re-redeem (on an edited name) is in flight.
  const [nameInput, setNameInput] = useState("");
  const [isJoining, setIsJoining] = useState(false);

  // Keep `code` in sync if the fragment changes without a full page reload
  // (e.g. pasting a different guest link). react-router does not re-render on a
  // pure hash change, so listen for it explicitly.
  useEffect(() => {
    const onHashChange = () => setCode(readGuestInviteCodeFromHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    const normalized = code ?? null;
    if (redeemedCodeRef.current === normalized) {
      return;
    }
    redeemedCodeRef.current = normalized;
    setState({ status: "loading" });
    // Reset any in-flight-join UI when the code changes so a leaked "joining"
    // state from a previous code can't disable the new code's pre-join screen.
    setIsJoining(false);

    if (!code) {
      setState({ status: "error" });
      return;
    }

    // No cleanup-cancel flag: the ref guard already ensures redeem runs exactly
    // once per code (incl. StrictMode's dev double-mount). A setState after a
    // real unmount is a silent no-op in React 18, so there is nothing to cancel.
    (async () => {
      try {
        const { token, room, inviteeName } = await redeemGuestInvite({
          code,
        });
        // STALE-GUARD (mirror of the re-redeem M3 guard): if the fragment
        // changed to a DIFFERENT code while this mount redeem was in flight,
        // the effect has already re-run for the new code — discard this stale
        // result instead of clobbering the new state (wrong prefill / joining
        // the old room with the old token).
        if (redeemedCodeRef.current !== code) {
          return;
        }
        if (!featureFlags.guestNameInput) {
          // Flag OFF: byte-identical to the original behavior — straight to
          // the Jitsi embed, no pre-join screen, exactly one redeem call.
          setState({ status: "joined", token, room });
          return;
        }
        setNameInput(inviteeName ?? "");
        setState({ status: "prejoin", code, token, room, inviteeName });
      } catch {
        // Same stale-guard on the failure path: a rejected redeem for a
        // superseded code must not flip the newer code's state to error.
        if (redeemedCodeRef.current !== code) {
          return;
        }
        // Never surface the specific failure reason (no enumeration oracle).
        setState({ status: "error" });
      }
    })();
  }, [code]);

  /**
   * Join from the pre-join screen. The mount-redeem token/room are always the
   * fallback; a SECOND redeem — carrying `displayName` — is only made if the
   * guest actually changed the name from its prefill (unedited or blank both
   * skip the extra network round trip and keep the "one redeem" invariant).
   * A rejected re-redeem NEVER blocks joining: it silently falls back to the
   * mount token (no error screen — the invite itself is still valid).
   */
  const handleJoin = async () => {
    if (state.status !== "prejoin" || isJoining) {
      return;
    }
    const { code: prejoinCode, token: mountToken, room, inviteeName } = state;
    // Trim BOTH sides: if the BE ever returns an untrimmed inviteeName, an
    // unedited prefill must still count as "unchanged" so it doesn't trigger a
    // needless 2nd redeem (which also consumes a join).
    const initial = (inviteeName ?? "").trim();
    const final = nameInput.trim();

    // KNOWN LIMITATION (impl-review M1): a re-redeem happens ONLY when the guest
    // enters a distinct non-empty name. Leaving the field unchanged OR clearing a
    // prefilled owner-set name both fall through to the mount token, so clearing a
    // prefilled name does NOT anonymize the guest — they join under the owner-set
    // name. This is intentional (avoids a needless 2nd join and an empty display
    // name); "clear to go anonymous" is out of scope here.
    if (final !== "" && final !== initial) {
      setIsJoining(true);
      try {
        const redeemed = await redeemGuestInvite({
          code: prejoinCode,
          displayName: final,
        });
        // STALE-GUARD (impl-review M3): if the URL fragment changed to a
        // different code while this re-redeem was in flight, the mount effect
        // has already taken over for the new code — discard this stale result
        // instead of clobbering the new loading/prejoin/error state.
        if (redeemedCodeRef.current !== prejoinCode) {
          return;
        }
        setState({
          status: "joined",
          token: redeemed.token,
          room: redeemed.room,
        });
        return;
      } catch {
        // Editing the name must never block a guest with an otherwise-valid
        // invite from joining — fall through to the mount token below. But if
        // the code changed mid-flight, don't clobber the newer state.
        if (redeemedCodeRef.current !== prejoinCode) {
          return;
        }
      } finally {
        // Only clear the joining lock if THIS re-redeem is still the current
        // one. A stale resolution (fragment changed to a new code mid-flight)
        // must not flip isJoining false while a newer re-redeem is in flight —
        // that would re-enable Join and risk a duplicate redeem.
        if (redeemedCodeRef.current === prejoinCode) {
          setIsJoining(false);
        }
      }
    }

    setState({ status: "joined", token: mountToken, room });
  };

  const meetDomain = useMemo(
    () => import.meta.env.VITE_MEET_DOMAIN || "meet.test.orionmessenger.io",
    [],
  );
  const configOverwrite = useMemo(
    () => ({
      disableInviteFunctions: true,
      disableProfile: true,
    }),
    [],
  );
  // Reuse the same curated toolbar the authenticated in-app call uses, so guests
  // don't get Jitsi's default permissive toolbar (jitsiToolbar.ts is the SSOT).
  // Guests can never use the Orion chat panel (no session), so they follow the same
  // flag: when Orion in-call chat is OFF (default) they get Jitsi's native chat button;
  // when ON, chat is left to the (guest-unavailable) Orion panel — parity with in-app.
  const interfaceConfigOverwrite = useMemo(
    () => ({
      TOOLBAR_BUTTONS: buildMeetToolbarButtons(!featureFlags.orionInCallChat),
    }),
    [],
  );

  if (state.status === "loading") {
    return <LoadingScreen title={t("guestInvite.joining")} />;
  }

  if (state.status === "error") {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-xl font-semibold">
          {t("guestInvite.invalidTitle")}
        </h1>
        <p className="max-w-md text-sm opacity-80">
          {t("guestInvite.invalidDescription")}
        </p>
      </div>
    );
  }

  if (state.status === "prejoin") {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <h1 className="text-xl font-semibold">
          {t("guestInvite.prejoinTitle")}
        </h1>
        <div className="flex w-full max-w-xs flex-col gap-2 text-left">
          <label htmlFor="guest-name-input" className="text-sm opacity-80">
            {t("guestInvite.yourName")}
          </label>
          <Input
            id="guest-name-input"
            value={nameInput}
            onChange={(event) => setNameInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void handleJoin();
              }
            }}
            placeholder={t("guestInvite.yourNamePlaceholder")}
            disabled={isJoining}
          />
        </div>
        <Button onClick={handleJoin} disabled={isJoining}>
          {t("guestInvite.joinButton")}
        </Button>
      </div>
    );
  }

  return (
    <div className="h-dvh w-full bg-black">
      <JitsiMeeting
        domain={meetDomain}
        roomName={state.room}
        jwt={state.token}
        configOverwrite={configOverwrite}
        interfaceConfigOverwrite={interfaceConfigOverwrite}
        getIFrameRef={(parentNode) => {
          if (parentNode) {
            parentNode.style.width = "100%";
            parentNode.style.height = "100%";
          }
        }}
      />
    </div>
  );
}
